import { Box, Button, CircularProgress, Dialog, keyframes, lighten, Link, Paper, ThemeProvider, Typography, Zoom } from '@mui/material'
import { TransitionProps } from '@mui/material/transitions'
import { FeatureFlagIdYearInReview2025 } from 'c1gen/features'
import { C1Button, C1LoadingButton } from 'c1ui'
import { C1BorderRadius } from 'c1ui/theme/C1BorderRadius'
import { C1DarkTheme } from 'c1ui/theme/C1Theme'
import { palette } from 'c1ui/theme/palette'
import { C1Icon } from 'components/core/icons/C1Icon'
import { useFeatureChecker } from 'components/no-ui/capability'
import { toPng } from 'html-to-image'
import { ChevronDown, Download, ExternalLink, X } from 'lucide-react'
import { YearInReviewMetricEntry } from 'pbts/c1/api/metrics/v1/metrics'
import React, { FC, forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { yearInReviewApi } from 'redux_toolkit/year_in_review'

const fadeInUp = keyframes`
    from {
        opacity: 0;
        transform: translateY(20px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
`

export const shimmer = keyframes`
    0% { background-position: -100% 0; }
    100% { background-position: 100% 0; }
`

const digitPop = keyframes`
    0% {
        transform: scale(1);
    }
    50% {
        transform: scale(1.25);
    }
    100% {
        transform: scale(1);
    }
`

const confettiFall = keyframes`
    0% {
        opacity: 1;
        transform: translateY(-30px) rotate(0deg);
    }
    5% {
        transform: translateY(0) rotate(20deg);
    }
    70% {
        opacity: 0.8;
    }
    100% {
        opacity: 0;
        transform: translateY(180px) rotate(400deg);
    }
`

const CONFETTI_COLORS = [
    palette.icons.turquoise,
    palette.icons.aqua,
    palette.icons.mauve,
    palette.icons.fuschia,
    palette.brand.main,
]

const ConfettiPiece: FC<{ index: number }> = ({ index }) => {
    const color = CONFETTI_COLORS[index % CONFETTI_COLORS.length]
    // Spread across the full width with some randomness
    const left = 3 + ((index * 17 + index * index) % 94)
    const delay = index * 0.06
    const duration = 2 + (index % 4) * 0.3
    const size = 5 + (index % 4) * 2
    const shape = index % 3 // 0 = circle, 1 = square, 2 = diamond

    return (
        <Box
            sx={{
                position: 'absolute',
                top: 0,
                left: `${left}%`,
                width: size,
                height: size,
                bgcolor: color,
                borderRadius: shape === 0 ? '50%' : shape === 2 ? 0 : '1px',
                animationName: `${confettiFall}`,
                animationDuration: `${duration}s`,
                animationTimingFunction: 'ease-out',
                animationDelay: `${delay}s`,
                animationFillMode: 'both',
                pointerEvents: 'none',
            }}
        />
    )
}

export const titleGradient = `linear-gradient(
    135deg,
    ${lighten(palette.icons.turquoise, 0.35)} 0%,
    ${lighten(palette.icons.turquoise, 0.3)} 15%,
    ${lighten(palette.icons.aqua, 0.3)} 30%,
    ${lighten(palette.icons.mauve, 0.3)} 50%,
    ${lighten(palette.icons.fuschia, 0.3)} 70%,
    ${lighten(palette.brand.main, 0.3)} 85%,
    ${lighten(palette.icons.turquoise, 0.35)} 100%
)`

const ModalTransition = forwardRef<unknown, TransitionProps & { children: React.ReactElement }>(
    (props, ref) => <Zoom ref={ref} {...props} timeout={{ enter: 400, exit: 0 }} />,
)

const AnimatedNumber: FC<{ value: number; duration?: number }> = ({ value, duration = 1500 }) => {
    const [display, setDisplay] = useState(0)

    useEffect(() => {
        if (value === 0) {
            setDisplay(0)
            return
        }

        const startTime = Date.now()
        const startValue = Math.max(0, value - 2000)
        let frameId: number | null = null

        const animate = (): void => {
            const elapsed = Date.now() - startTime
            const progress = Math.min(elapsed / duration, 1)
            // Use quintic ease-out for smoother deceleration at the end
            const eased = 1 - (1 - progress) ** 5
            const current = Math.round(startValue + (value - startValue) * eased)
            setDisplay(current)

            if (progress < 1) {
                frameId = requestAnimationFrame(animate)
            }
        }

        frameId = requestAnimationFrame(animate)

        return () => {
            if (frameId) cancelAnimationFrame(frameId)
        }
    }, [value, duration])

    return <>{display.toLocaleString()}</>
}

// =============================================================================
// Types
// =============================================================================

type StatConfig = {
    key: string
    label: string
    icon: string
    color: string
}

type SectionConfig = {
    title: string
    stats: StatConfig[]
}

// =============================================================================
// Helpers
// =============================================================================

const getMetricValue = (metrics: YearInReviewMetricEntry[], key: string): number => {
    const metric = metrics.find((m) => m.key === key)
    if (!metric) return 0
    if (metric.value.oneofKind === 'intValue') return Number(metric.value.intValue)
    return 0
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

const formatDays = (seconds: number): string => {
    if (seconds === 0) return '0'
    const days = seconds / 86400
    if (days < 1) return '<1'
    return Math.round(days).toString()
}

// =============================================================================
// 2025 Releases Configuration
// =============================================================================

const MAJOR_FEATURES_2025 = [
    'Agentic AI',
    'Automations',
    'Super Directory & Profile Types',
    'Microsoft Teams Integration',
    'Analytics Dashboard',
    'Campaign Dashboards',
    'Request Forms',
    'Role Mining',
    'Vaults',
    'Inventory Page',
    'Unused Access Automations',
    'Automatic Account Provisioning',
    'Application Admin Role',
    'New Home Page',
    'New Navigation Design',
    'Read-Only Admin Role',
    'Updated Access Reviews',
    'Customizable Columns',
    'Custom Avatars',
    'OoO Delegates',
]

const NEW_CONNECTORS_COUNT = 100

// Highlighted connectors shown by default
const HIGHLIGHTED_CONNECTORS = [
    'Adobe',
    'Amazon EKS',
    'Cursor',
    'Microsoft Teams',
    'PingOne',
    'Oracle Cloud',
    'Workday Account',
    'Azure DevOps',
]

// All new connectors (shown on expand)
const ALL_NEW_CONNECTORS = [
    ...HIGHLIGHTED_CONNECTORS,
    'Salesforce v2',
    'Zendesk v2',
    'GitLab v2',
    'RingCentral',
    'CouchDB',
    'Contentful',
    'Teleport',
    'ServiceNow',
    'Greenhouse',
    'Grafana',
    'Asana',
    'Incident.io',
    'HubSpot',
    'Datadog v2',
    'Google Workspace v2',
    'Okta v2',
    'GitHub v2',
    'Jira Data Center',
    'Slack Enterprise Grid',
    'Snyk',
]

const RELEASE_NOTES_URL = 'https://www.conductorone.com/docs/product/release-notes/'
const CONNECTOR_RELEASE_NOTES_URL = 'https://www.conductorone.com/docs/baton/_release-notes/'

// Minimum threshold to show a section
export const MIN_SECTION_THRESHOLD = 50

// =============================================================================
// Metric Configuration
// =============================================================================

// Sections ordered for narrative flow: grants → revokes → integrations
const SECTIONS: SectionConfig[] = [
    {
        title: 'Access grants',
        stats: [
            { key: 'total_requests_granted', label: 'Requests granted', icon: '✅', color: palette.icons.aqua },
            { key: 'auto_approval_grants', label: 'Auto-approved', icon: '⚡', color: palette.icons.turquoise },
            { key: 'auto_provision_grants', label: 'Auto-provisioned', icon: '🤖', color: palette.icons.aqua },
        ],
    },
    {
        title: 'Access revokes',
        stats: [
            { key: 'total_requests_revoked', label: 'Access revoked', icon: '🔒', color: palette.icons.mauve },
            { key: 'auto_approval_revokes', label: 'Auto-approved', icon: '⚡', color: palette.icons.fuschia },
            { key: 'auto_provision_revokes', label: 'Auto-provisioned', icon: '🤖', color: palette.icons.mauve },
        ],
    },
    {
        title: 'Integrations',
        stats: [
            { key: 'apps_connected', label: 'Apps connected', icon: '🔌', color: palette.icons.fuschia },
            { key: 'connector_syncs', label: 'Syncs completed', icon: '🔄', color: palette.icons.aqua },
        ],
    },
]

// Access Reviews section config (rendered separately with velocity)
const REVIEWS_STATS: StatConfig[] = [
    { key: 'campaigns_run', label: 'Campaigns', icon: '🎯', color: palette.icons.turquoise },
    { key: 'total_certifications', label: 'Tasks reviewed', icon: '📋', color: palette.brand.main },
    { key: 'reviews_completed_on_time', label: 'On time', icon: '⏱️', color: palette.icons.aqua },
]

// =============================================================================
// Styling Constants
// =============================================================================

const glowColor = palette.icons.turquoise

const GlowCard: FC<{
    children: React.ReactNode
    delay: number
    color: string
}> = ({ children, delay, color }) => {
    const [isHovering, setIsHovering] = useState(false)

    const handleMouseEnter = useCallback((): void => {
        setIsHovering(true)
    }, [])

    const handleMouseLeave = useCallback((): void => {
        setIsHovering(false)
    }, [])

    return (
        <Paper
            elevation={0}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            sx={{
                position: 'relative',
                bgcolor: 'rgba(255, 255, 255, 0.03)',
                borderRadius: 3,
                p: { xs: 3, sm: 4 },
                animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
                transition: 'transform 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease',
                border: '1px solid',
                borderColor: isHovering ? `${color}77` : `${color}55`,
                boxShadow: isHovering
                    ? `0 0 48px ${color}35, 0 8px 16px ${color}30, inset 0 1px 0 ${color}50`
                    : `0 0 32px ${color}25, 0 4px 12px ${color}20, inset 0 1px 0 ${color}40`,
                overflow: 'hidden',
                transform: isHovering ? 'translateY(-2px)' : 'none',
                '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    padding: '1px',
                    background: isHovering
                        ? `radial-gradient(circle at 50% 50%, ${glowColor}66, ${color}44, transparent 70%)`
                        : 'transparent',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                    pointerEvents: 'none',
                    transition: 'background 0.3s ease',
                },
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    background: isHovering
                        ? `radial-gradient(circle at 50% 50%, ${glowColor}08, transparent 70%)`
                        : 'transparent',
                    pointerEvents: 'none',
                    transition: 'background 0.3s ease',
                },
            }}
        >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
                {children}
            </Box>
        </Paper>
    )
}

const StatCell: FC<{
    config: StatConfig
    value: number
    delay: number
}> = ({ config, value, delay }) => (
    <GlowCard delay={delay} color={config.color}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: 2,
                        bgcolor: `${config.color}22`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        flexShrink: 0,
                    }}
                >
                    {config.icon}
                </Box>
                <Typography
                    variant="body2"
                    sx={{
                        color: 'rgba(255, 255, 255, 0.85)',
                        textTransform: 'uppercase',
                        letterSpacing: 1,
                        fontWeight: 500,
                    }}
                >
                    {config.label}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontWeight: 800,
                    color: 'text.primary',
                    lineHeight: 1,
                    fontVariantNumeric: 'tabular-nums',
                    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
                    letterSpacing: '-0.02em',
                }}
            >
                <AnimatedNumber value={value} duration={1500} />
            </Typography>
        </Box>
    </GlowCard>
)

// Section header component for visual separation
const SectionHeader: FC<{ title: string; subtitle?: string; delay: number; color?: string }> = ({
    title,
    subtitle,
    delay,
    color = palette.icons.turquoise,
}) => (
    <Box
        sx={{
            mb: 5,
            mt: 8,
            position: 'relative',
            zIndex: 2,
            animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
        }}
    >
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
            }}
        >
            <Box
                sx={{
                    width: 4,
                    height: 32,
                    background: `linear-gradient(180deg, ${color} 0%, ${color}40 100%)`,
                    borderRadius: 2,
                }}
            />
            <Typography
                variant="h2"
                sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                }}
            >
                {title}
            </Typography>
        </Box>
        {subtitle && (
            <Typography
                variant="body1"
                sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    ml: 4,
                }}
            >
                {subtitle}
            </Typography>
        )}
        <Box
            sx={{
                mt: 3,
                height: 1,
                background: `linear-gradient(90deg, ${color}50 0%, transparent 60%)`,
            }}
        />
    </Box>
)

// Hook to detect when element scrolls into view (works within dialog scroll containers)
const useScrollReveal = (): { ref: React.RefObject<HTMLDivElement | null>; isVisible: boolean } => {
    const ref = useRef<HTMLDivElement | null>(null)
    const [isVisible, setIsVisible] = useState(false)
    const hasTriggered = useRef(false)

    useEffect(() => {
        const element = ref.current
        if (!element || hasTriggered.current) return

        // Find the scrollable parent (dialog paper)
        const findScrollParent = (el: HTMLElement | null): HTMLElement | null => {
            while (el) {
                const style = getComputedStyle(el)
                if (style.overflow === 'auto' || style.overflowY === 'auto' || style.overflow === 'scroll') {
                    return el
                }
                el = el.parentElement
            }
            return null
        }

        const scrollParent = findScrollParent(element.parentElement)
        if (!scrollParent) return

        const checkVisibility = (): void => {
            if (!element || hasTriggered.current || !scrollParent) return

            const containerRect = scrollParent.getBoundingClientRect()
            const elementRect = element.getBoundingClientRect()

            // Calculate element position relative to the scroll container's visible area
            const elementTopRelativeToContainer = elementRect.top - containerRect.top

            // Trigger when element top is within 70% of the container's visible height
            const triggerThreshold = containerRect.height * 0.7
            const isInView = elementTopRelativeToContainer < triggerThreshold && elementRect.bottom > containerRect.top

            if (isInView) {
                hasTriggered.current = true
                setIsVisible(true)
            }
        }

        // Don't check immediately - only on scroll
        scrollParent.addEventListener('scroll', checkVisibility, { passive: true })

        return () => {
            scrollParent.removeEventListener('scroll', checkVisibility)
        }
    }, [])

    return { ref, isVisible }
}

// Component to animate each digit with pop effect on scroll
const WaveNumber: FC<{ value: number; isVisible: boolean }> = ({ value, isVisible }) => {
    const formatted = value.toLocaleString()
    const chars = formatted.split('')

    return (
        <Box component="span" sx={{ display: 'inline-flex' }}>
            {chars.map((char, idx) => (
                <Box
                    key={idx}
                    component="span"
                    sx={{
                        display: 'inline-block',
                        // Apply gradient to each character
                        background: titleGradient,
                        backgroundSize: '200% 200%',
                        backgroundClip: 'text',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        // Pop animation on scroll - always visible
                        animation: isVisible
                            ? `${digitPop} 0.5s ease-out forwards`
                            : 'none',
                        animationDelay: isVisible ? `${idx * 0.04}s` : '0s',
                    }}
                >
                    {char}
                </Box>
            ))}
        </Box>
    )
}

// Hero time saved component - shown right after title
const TimeSavedHero: FC<{ metrics: YearInReviewMetricEntry[]; delay: number }> = ({ metrics, delay }) => {
    // Time saved calculation - only include sections that meet MIN_SECTION_THRESHOLD:
    // - Grants: 10 mins base + 5 mins auto-approval + 10 mins auto-provision
    // - Revokes: 10 mins base + 5 mins auto-approval + 10 mins auto-provision
    // - Reviews: 1 min per certification + 20 hours per campaign
    const totalGranted = getMetricValue(metrics, 'total_requests_granted')
    const totalRevoked = getMetricValue(metrics, 'total_requests_revoked')
    const totalCertifications = getMetricValue(metrics, 'total_certifications')

    const grantsQualify = totalGranted >= MIN_SECTION_THRESHOLD
    const revokesQualify = totalRevoked >= MIN_SECTION_THRESHOLD
    const reviewsQualify = totalCertifications >= MIN_SECTION_THRESHOLD

    let totalSeconds = 0

    if (grantsQualify) {
        const autoApprovalGrants = getMetricValue(metrics, 'auto_approval_grants')
        const autoProvisionGrants = getMetricValue(metrics, 'auto_provision_grants')
        totalSeconds += totalGranted * 10 * 60 // 10 mins base
        totalSeconds += autoApprovalGrants * 5 * 60 // +5 mins auto-approval
        totalSeconds += autoProvisionGrants * 10 * 60 // +10 mins auto-provision
    }

    if (revokesQualify) {
        const autoApprovalRevokes = getMetricValue(metrics, 'auto_approval_revokes')
        const autoProvisionRevokes = getMetricValue(metrics, 'auto_provision_revokes')
        totalSeconds += totalRevoked * 10 * 60 // 10 mins base
        totalSeconds += autoApprovalRevokes * 5 * 60 // +5 mins auto-approval
        totalSeconds += autoProvisionRevokes * 10 * 60 // +10 mins auto-provision
    }

    if (reviewsQualify) {
        const campaigns = getMetricValue(metrics, 'campaigns_run')
        totalSeconds += totalCertifications * 1 * 60 // 1 min per certification
        totalSeconds += campaigns * 20 * 3600 // 20 hours per campaign
    }

    const totalHours = Math.round(totalSeconds / 3600)

    const workWeeks = Math.round(totalHours / 40)
    const workDays = Math.round(totalHours / 8)

    // Use scroll reveal for celebratory animation - must be called before any returns
    const { ref: heroRef, isVisible } = useScrollReveal()

    if (totalHours === 0) return null

    return (
        <Box
            ref={heroRef}
            sx={{
                textAlign: 'center',
                pt: 4,
                mb: 6,
                pb: 4,
                position: 'relative',
                zIndex: 2,
                animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
            }}
        >
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)', mb: 2, textTransform: 'uppercase', letterSpacing: 2, fontSize: '0.85rem', fontWeight: 500 }}>
                <FormattedMessage id="year_in_review_time_saved_label" defaultMessage="Time saved with automation" />
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography
                    component="span"
                    sx={{
                        fontWeight: 800,
                        fontSize: { xs: '4rem', sm: '5rem', md: '6rem' },
                        letterSpacing: '-0.03em',
                        lineHeight: 1,
                    }}
                >
                    <WaveNumber value={totalHours} isVisible={isVisible} />
                </Typography>
                <Typography
                    sx={{
                        color: 'rgba(255, 255, 255, 0.75)',
                        fontWeight: 400,
                        fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                    }}
                >
                    <FormattedMessage id="year_in_review_hours" defaultMessage="hours" />
                </Typography>
            </Box>
            {workWeeks >= 1 ? (
                <Typography
                    sx={{
                        mt: 2,
                        color: palette.icons.turquoise,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                    }}
                >
                    <FormattedMessage
                        id="year_in_review_work_weeks"
                        defaultMessage="That's ~{weeks} work {weeks, plural, one {week} other {weeks}} your team got back!"
                        values={{ weeks: workWeeks }}
                    />
                    {' '}
                    🎉
                </Typography>
            ) : workDays > 1 ? (
                <Typography
                    sx={{
                        mt: 2,
                        color: palette.icons.turquoise,
                        fontWeight: 600,
                        fontSize: '1.1rem',
                    }}
                >
                    <FormattedMessage
                        id="year_in_review_work_days"
                        defaultMessage="That's ~{days} work {days, plural, one {day} other {days}} your team got back!"
                        values={{ days: workDays }}
                    />
                    {' '}
                    🎉
                </Typography>
            ) : null}
        </Box>
    )
}

// Achievement badge definitions
type AchievementDef = {
    id: string
    icon: string
    title: string
    description: string
    color: string
    check: (metrics: YearInReviewMetricEntry[]) => boolean
}

const ACHIEVEMENT_DEFINITIONS: AchievementDef[] = [
    {
        id: 'speed_demon',
        icon: '⚡',
        title: 'Speed Demon',
        description: '25%+ reviews done in 1 day',
        color: palette.icons.turquoise,
        check: (metrics): boolean => getMetricValue(metrics, 'avg_completion_pct_1_day') >= 25,
    },
    {
        id: 'automation_pro',
        icon: '🤖',
        title: 'Automation Pro',
        description: '50%+ auto-approved',
        color: palette.icons.aqua,
        check: (metrics): boolean => {
            const totalGrants = getMetricValue(metrics, 'total_requests_granted')
            const autoGrants = getMetricValue(metrics, 'auto_approval_grants')
            return totalGrants > 0 && autoGrants / totalGrants >= 0.5
        },
    },
    {
        id: 'review_champion',
        icon: '🏆',
        title: 'Review Champion',
        description: '90%+ reviews on-time',
        color: palette.brand.main,
        check: (metrics): boolean => {
            const totalCerts = getMetricValue(metrics, 'total_certifications')
            const onTime = getMetricValue(metrics, 'reviews_completed_on_time')
            return totalCerts > 0 && onTime / totalCerts >= 0.9
        },
    },
    {
        id: 'scale_master',
        icon: '📈',
        title: 'Scale Master',
        description: '1000+ total requests',
        color: palette.icons.fuschia,
        check: (metrics): boolean => {
            const totalGrants = getMetricValue(metrics, 'total_requests_granted')
            const totalRevokes = getMetricValue(metrics, 'total_requests_revoked')
            return totalGrants + totalRevokes >= 1000
        },
    },
]

// Achievements row component
const AchievementsRow: FC<{ metrics: YearInReviewMetricEntry[]; delay: number }> = ({ metrics, delay }) => {
    const earnedAchievements = ACHIEVEMENT_DEFINITIONS.filter((a) => a.check(metrics))

    if (earnedAchievements.length === 0) return null

    return (
        <Box
            sx={{
                mb: 5,
                position: 'relative',
                zIndex: 2,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    display: 'block',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mb: 2,
                    animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
                }}
            >
                <FormattedMessage id="year_in_review_achievements" defaultMessage="Achievements unlocked" />
            </Typography>
            <Box
                sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                }}
            >
                {earnedAchievements.map((achievement, idx) => (
                    <Box
                        key={achievement.id}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2,
                            bgcolor: `${achievement.color}15`,
                            border: '1px solid',
                            borderColor: `${achievement.color}40`,
                            borderRadius: 3,
                            px: 3,
                            py: 2,
                            animation: `${fadeInUp} 0.4s ease-out ${delay + 50 + idx * 80}ms both`,
                            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            '&:hover': {
                                transform: 'translateY(-2px)',
                                boxShadow: `0 8px 24px ${achievement.color}30`,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                bgcolor: `${achievement.color}25`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: 24,
                            }}
                        >
                            {achievement.icon}
                        </Box>
                        <Box>
                            <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.95rem' }}>
                                {achievement.title}
                            </Typography>
                            <Typography sx={{ color: 'rgba(255, 255, 255, 0.75)', fontSize: '0.75rem' }}>
                                {achievement.description}
                            </Typography>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    )
}

// Monthly peak callout
const MonthlyPeakCallout: FC<{ metrics: YearInReviewMetricEntry[]; delay: number }> = ({ metrics, delay }) => {
    const peakMonth = getMetricValue(metrics, 'peak_month')
    const peakMonthCount = getMetricValue(metrics, 'peak_month_count')

    if (peakMonth === 0 || peakMonthCount === 0) return null

    const monthName = MONTH_NAMES[peakMonth - 1]

    return (
        <Box
            sx={{
                mb: 6,
                bgcolor: `${palette.icons.mauve}12`,
                border: '1px solid',
                borderColor: `${palette.icons.mauve}30`,
                borderRadius: 3,
                p: 4,
                textAlign: 'center',
                position: 'relative',
                zIndex: 2,
                animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
            }}
        >
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.9rem', mb: 1 }}>
                <FormattedMessage id="year_in_review_busiest_month" defaultMessage="Your busiest month was" />
            </Typography>
            <Typography
                sx={{
                    fontWeight: 800,
                    color: palette.icons.mauve,
                    fontSize: { xs: '1.75rem', sm: '2.25rem' },
                    letterSpacing: '-0.02em',
                }}
            >
                {monthName}
            </Typography>
            <Typography sx={{ color: 'rgba(255, 255, 255, 0.85)', fontSize: '0.9rem', mt: 1 }}>
                <FormattedMessage
                    id="year_in_review_busiest_month_count"
                    defaultMessage="with {count} requests processed"
                    values={{ count: peakMonthCount.toLocaleString() }}
                />
            </Typography>
        </Box>
    )
}

// Hero component showing 3 key stats: features, connectors, hours saved
const HeroStatsRow: FC<{ delay: number }> = ({ delay }) => {
    const featureCount = 20
    const connectorCount = NEW_CONNECTORS_COUNT

    const heroStats = [
        { value: `${featureCount}+`, label: 'Major Features', color: palette.icons.turquoise },
        { value: `${connectorCount}+`, label: 'New Connectors', color: palette.icons.aqua },
    ]

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'center',
                gap: { xs: 4, sm: 6 },
                mb: 6,
                position: 'relative',
                zIndex: 2,
                animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
            }}
        >
            {heroStats.map((stat, idx) => (
                <Box
                    key={stat.label}
                    sx={{
                        textAlign: 'center',
                        animation: `${fadeInUp} 0.4s ease-out ${delay + idx * 100}ms both`,
                        flex: { sm: 1 },
                        px: { sm: 2 },
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 800,
                            background: titleGradient,
                            backgroundSize: '200% 200%',
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontSize: { xs: '3rem', sm: '4rem', md: '5rem' },
                            letterSpacing: '-0.03em',
                            lineHeight: 1,
                        }}
                    >
                        {stat.value}
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            display: 'block',
                            color: 'rgba(255, 255, 255, 0.85)',
                            textTransform: 'uppercase',
                            letterSpacing: 1.5,
                            mt: 2,
                            fontWeight: 500,
                        }}
                    >
                        {stat.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    )
}

// Features section showing what we shipped in 2025
const FeaturesSection: FC<{ delay: number }> = ({ delay }) => (
    <Box
        sx={{
            mb: 6,
            position: 'relative',
            zIndex: 2,
        }}
    >
        <Typography
            variant="h6"
            sx={{
                display: 'block',
                color: 'rgba(255, 255, 255, 0.95)',
                fontSize: '1.1rem',
                fontWeight: 600,
                mb: 1,
                animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
            }}
        >
            <FormattedMessage id="year_in_review_features_title" defaultMessage="Major features shipped" />
        </Typography>
        <Typography
            variant="body2"
            sx={{
                display: 'block',
                color: 'rgba(255, 255, 255, 0.7)',
                mb: 3,
                animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
            }}
        >
            <FormattedMessage id="year_in_review_features_subtitle" defaultMessage="What we built together this year" />
        </Typography>
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                gap: 2,
            }}
        >
            {MAJOR_FEATURES_2025.map((feature, idx) => (
                <Box
                    key={feature}
                    sx={{
                        bgcolor: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: 2,
                        px: 2,
                        py: 2,
                        border: '1px solid',
                        borderColor: `${idx % 2 === 0 ? palette.icons.turquoise : palette.icons.aqua}25`,
                        boxShadow: `0 2px 8px ${idx % 2 === 0 ? palette.icons.turquoise : palette.icons.aqua}10`,
                        animation: `${fadeInUp} 0.3s ease-out ${delay + 50 + idx * 30}ms both`,
                        transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        '&:hover': {
                            bgcolor: 'rgba(255, 255, 255, 0.06)',
                            borderColor: `${idx % 2 === 0 ? palette.icons.turquoise : palette.icons.aqua}40`,
                            boxShadow: `0 4px 16px ${idx % 2 === 0 ? palette.icons.turquoise : palette.icons.aqua}20`,
                            transform: 'translateX(4px)',
                        },
                    }}
                >
                    <Box
                        sx={{
                            width: 6,
                            height: 6,
                            borderRadius: '50%',
                            bgcolor: idx % 2 === 0 ? palette.icons.turquoise : palette.icons.aqua,
                            flexShrink: 0,
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.primary',
                            fontWeight: 500,
                            fontSize: '0.8rem',
                        }}
                    >
                        {feature}
                    </Typography>
                </Box>
            ))}
        </Box>
        <Link
            href={RELEASE_NOTES_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1,
                color: palette.icons.turquoise,
                textDecoration: 'none',
                fontSize: '0.75rem',
                mt: 2,
                '&:hover': {
                    textDecoration: 'underline',
                },
            }}
        >
            <FormattedMessage id="year_in_review_release_notes" defaultMessage="View all release notes" />
            <C1Icon icon={ExternalLink} size="sm" />
        </Link>
    </Box>
)

// New Connectors section with expand/collapse
const NewConnectorsSection: FC<{ delay: number }> = ({ delay }) => {
    const [expanded, setExpanded] = useState(false)
    const displayedConnectors = expanded ? ALL_NEW_CONNECTORS : HIGHLIGHTED_CONNECTORS

    return (
        <Box
            sx={{
                mb: 6,
                position: 'relative',
                zIndex: 2,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    display: 'block',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mb: 1,
                    animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
                }}
            >
                <FormattedMessage id="year_in_review_connectors_title" defaultMessage="New connectors" />
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    display: 'block',
                    color: 'rgba(255, 255, 255, 0.7)',
                    mb: 3,
                    animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
                }}
            >
                <FormattedMessage
                    id="year_in_review_connectors_subtitle"
                    defaultMessage="100+ new integrations added in 2025"
                />
            </Typography>

            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                    gap: 2,
                }}
            >
                {displayedConnectors.map((connector, idx) => (
                    <Box
                        key={connector}
                        sx={{
                            bgcolor: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: 2,
                            px: 2,
                            py: 2,
                            border: '1px solid',
                            borderColor: `${idx % 2 === 0 ? palette.icons.fuschia : palette.icons.mauve}25`,
                            boxShadow: `0 2px 8px ${idx % 2 === 0 ? palette.icons.fuschia : palette.icons.mauve}10`,
                            animation: `${fadeInUp} 0.3s ease-out ${delay + 50 + idx * 30}ms both`,
                            transition: 'background-color 0.2s ease, border-color 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.06)',
                                borderColor: `${idx % 2 === 0 ? palette.icons.fuschia : palette.icons.mauve}40`,
                                boxShadow: `0 4px 16px ${idx % 2 === 0 ? palette.icons.fuschia : palette.icons.mauve}20`,
                                transform: 'translateX(4px)',
                            },
                        }}
                    >
                        <Box
                            sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: idx % 2 === 0 ? palette.icons.fuschia : palette.icons.mauve,
                                flexShrink: 0,
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: 'text.primary',
                                fontWeight: 500,
                                fontSize: '0.8rem',
                            }}
                        >
                            {connector}
                        </Typography>
                    </Box>
                ))}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: 2,
                    animation: `${fadeInUp} 0.4s ease-out ${delay + 300}ms both`,
                }}
            >
                <Button
                    onClick={(): void => setExpanded(!expanded)}
                    endIcon={
                        <C1Icon
                            icon={ChevronDown}
                            sx={{
                                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.2s ease',
                            }}
                        />
                    }
                    sx={{
                        color: 'text.secondary',
                        textTransform: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                            color: 'text.primary',
                            bgcolor: 'rgba(255, 255, 255, 0.05)',
                        },
                    }}
                >
                    {expanded ? (
                        <FormattedMessage id="year_in_review_show_less" defaultMessage="Show less" />
                    ) : (
                        <FormattedMessage
                            id="year_in_review_show_more"
                            defaultMessage="Show {count} more"
                            values={{ count: ALL_NEW_CONNECTORS.length - HIGHLIGHTED_CONNECTORS.length }}
                        />
                    )}
                </Button>

                <Link
                    href={CONNECTOR_RELEASE_NOTES_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        color: palette.icons.turquoise,
                        textDecoration: 'none',
                        fontSize: '0.75rem',
                        '&:hover': {
                            textDecoration: 'underline',
                        },
                    }}
                >
                    <FormattedMessage id="year_in_review_view_release_notes" defaultMessage="View connector release notes" />
                    <C1Icon icon={ExternalLink} size="sm" />
                </Link>
            </Box>
        </Box>
    )
}

// Combined Access Reviews section with stats and velocity
const AccessReviewsSection: FC<{
    metrics: YearInReviewMetricEntry[]
    delay: number
}> = ({ metrics, delay }) => {
    const totalCertifications = getMetricValue(metrics, 'total_certifications')
    if (totalCertifications < MIN_SECTION_THRESHOLD) return null

    const avg90PctSeconds = getMetricValue(metrics, 'campaigns_90_percent_avg_seconds')
    const pct1Day = getMetricValue(metrics, 'avg_completion_pct_1_day')
    const pct3Days = getMetricValue(metrics, 'avg_completion_pct_3_days')
    const pct1Week = getMetricValue(metrics, 'avg_completion_pct_1_week')
    const hasVelocityData = avg90PctSeconds > 0 || pct1Day > 0 || pct3Days > 0 || pct1Week > 0

    return (
        <Box
            sx={{
                mb: 6,
                position: 'relative',
                zIndex: 2,
            }}
        >
            <Typography
                variant="h6"
                sx={{
                    display: 'block',
                    color: 'rgba(255, 255, 255, 0.95)',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    mb: 1,
                    animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
                }}
            >
                <FormattedMessage id="year_in_review_access_reviews" defaultMessage="Access reviews" />
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    display: 'block',
                    color: 'rgba(255, 255, 255, 0.7)',
                    mb: 3,
                    animation: `${fadeInUp} 0.4s ease-out ${delay}ms both`,
                }}
            >
                <FormattedMessage
                    id="year_in_review_reviews_subtitle"
                    defaultMessage="Compliance and governance campaigns"
                />
            </Typography>

            {/* Stats row */}
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' },
                    gap: 3,
                    mb: hasVelocityData ? 4 : 0,
                }}
            >
                {REVIEWS_STATS.map((stat, idx) => (
                    <StatCell
                        key={stat.key}
                        config={stat}
                        value={getMetricValue(metrics, stat.key)}
                        delay={delay + 50 + idx * 80}
                    />
                ))}
            </Box>

            {/* Velocity metrics - separated into hero stat and progress breakdown */}
            {hasVelocityData && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 3,
                        animation: `${fadeInUp} 0.4s ease-out ${delay + 300}ms both`,
                    }}
                >
                    {/* Hero: Time to 90% */}
                    {avg90PctSeconds > 0 && (
                        <Box
                            sx={{
                                flex: { md: '0 0 280px' },
                                bgcolor: `${palette.icons.turquoise}12`,
                                borderRadius: 3,
                                p: 4,
                                border: '1px solid',
                                borderColor: `${palette.icons.turquoise}40`,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                            }}
                        >
                            <Typography
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.8)',
                                    fontSize: '0.9rem',
                                    mb: 1,
                                }}
                            >
                                <FormattedMessage id="year_in_review_avg_time_to_90" defaultMessage="Average time to 90% complete" />
                            </Typography>
                            <Typography
                                sx={{
                                    fontWeight: 800,
                                    color: palette.icons.turquoise,
                                    fontSize: { xs: '3.5rem', sm: '4rem' },
                                    lineHeight: 1,
                                }}
                            >
                                {formatDays(avg90PctSeconds)}
                            </Typography>
                            <Typography
                                sx={{
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    fontSize: '1.1rem',
                                    mt: 1,
                                }}
                            >
                                <FormattedMessage id="year_in_review_days" defaultMessage="days" />
                            </Typography>
                        </Box>
                    )}

                    {/* Completion progress breakdown */}
                    <Box
                        sx={{
                            flex: 1,
                            bgcolor: 'rgba(255, 255, 255, 0.03)',
                            borderRadius: 3,
                            p: 4,
                            border: '1px solid',
                            borderColor: 'rgba(255, 255, 255, 0.1)',
                        }}
                    >
                        <Typography
                            variant="h6"
                            sx={{
                                color: 'rgba(255, 255, 255, 0.95)',
                                fontSize: '1rem',
                                fontWeight: 600,
                                mb: 3,
                            }}
                        >
                            <FormattedMessage id="year_in_review_completion_rate" defaultMessage="Average completion rate" />
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            {/* Day 1 */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                                        <FormattedMessage id="year_in_review_after_1_day" defaultMessage="After 1 day" />
                                    </Typography>
                                    <Typography sx={{ fontWeight: 700, color: palette.icons.aqua, fontSize: '1.25rem' }}>
                                        <AnimatedNumber value={pct1Day} duration={1500} />
                                        %
                                    </Typography>
                                </Box>
                                <Box sx={{ height: 8, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 4, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: `${pct1Day}%`, bgcolor: palette.icons.aqua, borderRadius: 4, transition: 'width 0.8s ease-out' }} />
                                </Box>
                            </Box>

                            {/* Day 3 */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                                        <FormattedMessage id="year_in_review_after_3_days" defaultMessage="After 3 days" />
                                    </Typography>
                                    <Typography sx={{ fontWeight: 700, color: palette.icons.turquoise, fontSize: '1.25rem' }}>
                                        <AnimatedNumber value={pct3Days} duration={1500} />
                                        %
                                    </Typography>
                                </Box>
                                <Box sx={{ height: 8, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 4, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: `${pct3Days}%`, bgcolor: palette.icons.turquoise, borderRadius: 4, transition: 'width 0.9s ease-out' }} />
                                </Box>
                            </Box>

                            {/* Week 1 */}
                            <Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 1 }}>
                                    <Typography sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.9rem' }}>
                                        <FormattedMessage id="year_in_review_after_1_week" defaultMessage="After 1 week" />
                                    </Typography>
                                    <Typography sx={{ fontWeight: 700, color: palette.brand.main, fontSize: '1.25rem' }}>
                                        <AnimatedNumber value={pct1Week} duration={1500} />
                                        %
                                    </Typography>
                                </Box>
                                <Box sx={{ height: 8, bgcolor: 'rgba(255, 255, 255, 0.1)', borderRadius: 4, overflow: 'hidden' }}>
                                    <Box sx={{ height: '100%', width: `${pct1Week}%`, bgcolor: palette.brand.main, borderRadius: 4, transition: 'width 1s ease-out' }} />
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            )}
        </Box>
    )
}

// Helper to format large numbers compactly
const formatCompactNumber = (value: number): string => {
    if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1).replace(/\.0$/, '')}M`
    }
    if (value >= 10000) {
        return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`
    }
    return value.toLocaleString()
}

// Shareable card component for LinkedIn (1200x1200 square)
const ShareableCard: FC<{ metrics: YearInReviewMetricEntry[] }> = ({ metrics }) => {
    const totalGranted = getMetricValue(metrics, 'total_requests_granted')
    const totalRevoked = getMetricValue(metrics, 'total_requests_revoked')
    const totalCertifications = getMetricValue(metrics, 'total_certifications')

    const grantsQualify = totalGranted >= MIN_SECTION_THRESHOLD
    const revokesQualify = totalRevoked >= MIN_SECTION_THRESHOLD
    const reviewsQualify = totalCertifications >= MIN_SECTION_THRESHOLD

    // Calculate automation percentages
    // Use max of auto-approval vs auto-provision since a ticket can be both
    const autoApprovalGrants = getMetricValue(metrics, 'auto_approval_grants')
    const autoProvisionGrants = getMetricValue(metrics, 'auto_provision_grants')
    const autoApprovalRevokes = getMetricValue(metrics, 'auto_approval_revokes')
    const autoProvisionRevokes = getMetricValue(metrics, 'auto_provision_revokes')
    const reviewsOnTime = getMetricValue(metrics, 'reviews_completed_on_time')

    // Use max of the two since they can overlap (a request can be both auto-approved AND auto-provisioned)
    const grantsAutomatedPct = totalGranted > 0
        ? Math.min(100, Math.round((Math.max(autoApprovalGrants, autoProvisionGrants) / totalGranted) * 100))
        : 0
    const revokesAutomatedPct = totalRevoked > 0
        ? Math.min(100, Math.round((Math.max(autoApprovalRevokes, autoProvisionRevokes) / totalRevoked) * 100))
        : 0
    const reviewsOnTimePct = totalCertifications > 0
        ? Math.min(100, Math.round((reviewsOnTime / totalCertifications) * 100))
        : 0

    // Calculate time saved (same logic as TimeSavedHero)
    let totalSeconds = 0
    if (grantsQualify) {
        totalSeconds += totalGranted * 10 * 60
        totalSeconds += autoApprovalGrants * 5 * 60
        totalSeconds += autoProvisionGrants * 10 * 60
    }
    if (revokesQualify) {
        totalSeconds += totalRevoked * 10 * 60
        totalSeconds += autoApprovalRevokes * 5 * 60
        totalSeconds += autoProvisionRevokes * 10 * 60
    }
    if (reviewsQualify) {
        const campaigns = getMetricValue(metrics, 'campaigns_run')
        totalSeconds += totalCertifications * 1 * 60
        totalSeconds += campaigns * 20 * 3600
    }
    const totalHours = Math.round(totalSeconds / 3600)
    const workWeeks = Math.round(totalHours / 40)
    const workDays = Math.round(totalHours / 8)

    const earnedAchievements = ACHIEVEMENT_DEFINITIONS.filter((a) => a.check(metrics))

    // Minimum threshold to show automation percentage
    const MIN_AUTOMATION_PCT_THRESHOLD = 25

    type StatItem = {
        label: string
        value: number
        icon: string
        color: string
        automatedPct?: number
        automatedLabel?: string
    }

    const statItems: StatItem[] = [
        grantsQualify && {
            label: 'Requests granted',
            value: totalGranted,
            icon: '✅',
            color: palette.icons.aqua,
            automatedPct: grantsAutomatedPct >= MIN_AUTOMATION_PCT_THRESHOLD ? grantsAutomatedPct : undefined,
            automatedLabel: 'automated',
        },
        revokesQualify && {
            label: 'Access revoked',
            value: totalRevoked,
            icon: '🔒',
            color: palette.icons.mauve,
            automatedPct: revokesAutomatedPct >= MIN_AUTOMATION_PCT_THRESHOLD ? revokesAutomatedPct : undefined,
            automatedLabel: 'automated',
        },
        reviewsQualify && {
            label: 'Reviews completed',
            value: totalCertifications,
            icon: '📋',
            color: palette.icons.turquoise,
            automatedPct: reviewsOnTimePct >= MIN_AUTOMATION_PCT_THRESHOLD ? reviewsOnTimePct : undefined,
            automatedLabel: 'on time',
        },
    ].filter(Boolean) as StatItem[]

    return (
        <Box
            sx={{
                width: 1200,
                height: 1200,
                bgcolor: palette.brand.dark,
                background: `
                    radial-gradient(ellipse 1200px 900px at 50% 20%, ${palette.icons.turquoise}15 0%, transparent 50%),
                    radial-gradient(ellipse 800px 800px at 100% 100%, ${palette.icons.mauve}12 0%, transparent 45%),
                    linear-gradient(180deg, ${palette.brand.dark} 0%, #1a1a3e 100%)
                `,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: '80px',
                boxSizing: 'border-box',
            }}
        >
            {/* Top Section: Title + Powered by */}
            <Box sx={{ textAlign: 'center' }}>
                <Box
                    component="img"
                    src="/dapi/static/images/branding/logo-white/current/256"
                    alt="ConductorOne"
                    sx={{ height: 48, width: 'auto', mb: '24px' }}
                />
                <Typography
                    sx={{
                        fontWeight: 800,
                        background: titleGradient,
                        backgroundSize: '200% 100%',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        fontSize: 80,
                        letterSpacing: '-0.03em',
                        lineHeight: 1.2,
                    }}
                >
                    2025: Reviewed
                </Typography>
            </Box>

            {/* Middle Section: Time Saved Hero */}
            {totalHours > 0 && (
                <Box
                    sx={{
                        textAlign: 'center',
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        py: '24px',
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 900,
                            color: '#fff',
                            fontSize: 180,
                            letterSpacing: '-0.04em',
                            lineHeight: 1,
                        }}
                    >
                        {formatCompactNumber(totalHours)}
                    </Typography>
                    <Typography
                        sx={{
                            color: '#fff',
                            fontSize: 44,
                            fontWeight: 500,
                            letterSpacing: 6,
                            textTransform: 'uppercase',
                            mt: '20px',
                        }}
                    >
                        hours saved
                    </Typography>
                    {workWeeks >= 1 ? (
                        <Typography
                            sx={{
                                color: palette.icons.turquoise,
                                fontSize: 32,
                                fontWeight: 600,
                                mt: '28px',
                            }}
                        >
                            ~
                            {workWeeks}
                            {' '}
                            work
                            {' '}
                            {workWeeks === 1 ? 'week' : 'weeks'}
                            {' '}
                            reclaimed 🎉
                        </Typography>
                    ) : workDays > 1 ? (
                        <Typography
                            sx={{
                                color: palette.icons.turquoise,
                                fontSize: 32,
                                fontWeight: 600,
                                mt: '28px',
                            }}
                        >
                            ~
                            {workDays}
                            {' '}
                            work
                            {' '}
                            {workDays === 1 ? 'day' : 'days'}
                            {' '}
                            reclaimed 🎉
                        </Typography>
                    ) : null}
                </Box>
            )}

            {/* Bottom Section: Stats Grid + Achievements + Footer */}
            <Box sx={{ textAlign: 'center', width: '100%' }}>
                {/* Stats 2x2 Grid with card backgrounds */}
                {statItems.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '24px',
                            mb: '28px',
                            flexWrap: 'wrap',
                        }}
                    >
                        {statItems.map((stat) => (
                            <Box
                                key={stat.label}
                                sx={{
                                    textAlign: 'center',
                                    bgcolor: 'rgba(255, 255, 255, 0.04)',
                                    borderRadius: '20px',
                                    py: '28px',
                                    px: '20px',
                                    border: '1px solid rgba(255, 255, 255, 0.08)',
                                    minWidth: 280,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '12px',
                                        mb: '8px',
                                    }}
                                >
                                    <Box sx={{ fontSize: 36 }}>{stat.icon}</Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 52,
                                            color: '#fff',
                                            lineHeight: 1.1,
                                        }}
                                    >
                                        {formatCompactNumber(stat.value)}
                                    </Typography>
                                </Box>
                                <Typography
                                    sx={{
                                        color: '#fff',
                                        fontSize: 24,
                                        fontWeight: 500,
                                        textTransform: 'uppercase',
                                        letterSpacing: 1,
                                    }}
                                >
                                    {stat.label}
                                </Typography>
                                {stat.automatedPct !== undefined && (
                                    <Typography
                                        sx={{
                                            color: lighten(stat.color, 0.35),
                                            fontSize: 26,
                                            fontWeight: 700,
                                            mt: '12px',
                                        }}
                                    >
                                        {stat.automatedPct}
                                        %
                                        {' '}
                                        {stat.automatedLabel}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Achievements - Refined Badge */}
                {earnedAchievements.length > 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '20px',
                            mb: '28px',
                            flexWrap: 'wrap',
                        }}
                    >
                        {earnedAchievements.map((achievement) => (
                            <Box
                                key={achievement.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '16px',
                                    bgcolor: `${achievement.color}18`,
                                    border: '2px solid',
                                    borderColor: `${achievement.color}50`,
                                    borderRadius: '16px',
                                    px: '28px',
                                    py: '16px',
                                }}
                            >
                                <Box sx={{ fontSize: 32 }}>{achievement.icon}</Box>
                                <Box>
                                    <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: 26 }}>
                                        {achievement.title}
                                    </Typography>
                                    <Typography sx={{ color: '#fff', fontSize: 24 }}>
                                        {achievement.description}
                                    </Typography>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Footer */}
                <Typography
                    sx={{
                        color: '#fff',
                        fontSize: 28,
                        fontWeight: 400,
                    }}
                >
                    Here's to
                    {' '}
                    <Typography
                        component="span"
                        sx={{
                            background: titleGradient,
                            backgroundClip: 'text',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 800,
                            fontSize: 'inherit',
                        }}
                    >
                        2026
                    </Typography>
                    {' '}
                    🚀
                </Typography>
            </Box>
        </Box>
    )
}

interface YearInReviewContentProps {
    metrics: YearInReviewMetricEntry[]
    showDownload?: boolean
}

export const YearInReviewContent: FC<YearInReviewContentProps> = ({ metrics, showDownload = true }) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const shareableRef = useRef<HTMLDivElement>(null)
    const [isDownloading, setIsDownloading] = useState(false)

    const handleDownload = useCallback(async (): Promise<void> => {
        if (!shareableRef.current || isDownloading) return

        setIsDownloading(true)
        try {
            // Temporarily make the shareable card visible for capture
            const wrapper = shareableRef.current
            wrapper.style.opacity = '1'
            wrapper.style.zIndex = '9999'

            const dataUrl = await toPng(shareableRef.current.firstElementChild as HTMLElement, {
                pixelRatio: 2,
                backgroundColor: palette.brand.dark,
                width: 1200,
                height: 1200,
                fontEmbedCSS: `
                    * {
                        font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif !important;
                    }
                `,
            })

            // Restore hidden state
            wrapper.style.opacity = '0'
            wrapper.style.zIndex = '-1'

            const link = document.createElement('a')
            link.download = 'conductorone-2025-reviewed.png'
            link.href = dataUrl
            link.click()
        } catch (error) {
            console.error('Failed to generate image:', error)
            // Restore hidden state on error
            if (shareableRef.current) {
                shareableRef.current.style.opacity = '0'
                shareableRef.current.style.zIndex = '-1'
            }
        } finally {
            setIsDownloading(false)
        }
    }, [isDownloading])

    return (
        <>
            {/* Hidden shareable card for download */}
            <Box
                ref={shareableRef}
                sx={{
                    position: 'fixed',
                    left: 0,
                    top: 0,
                    opacity: 0,
                    pointerEvents: 'none',
                    zIndex: -1,
                }}
            >
                <ThemeProvider theme={C1DarkTheme}>
                    <ShareableCard metrics={metrics} />
                </ThemeProvider>
            </Box>

            <Box
                ref={containerRef}
                data-year-in-review
                sx={{
                    width: '100%',
                    bgcolor: 'transparent',
                    py: { xs: 3, sm: 4, md: 5 },
                    px: { xs: 2, sm: 3, md: 5 },
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: -200,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 800,
                        height: 800,
                        background: `radial-gradient(circle, ${palette.icons.turquoise}12, transparent 70%)`,
                        pointerEvents: 'none',
                        zIndex: 0,
                        opacity: 0.6,
                    },
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -200,
                        right: -200,
                        width: 600,
                        height: 600,
                        background: `radial-gradient(circle, ${palette.icons.mauve}10, transparent 70%)`,
                        pointerEvents: 'none',
                        zIndex: 0,
                        opacity: 0.5,
                    },
                }}
            >
                {/* Confetti burst from top */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 200,
                        pointerEvents: 'none',
                        zIndex: 10,
                    }}
                >
                    {[...Array(15)].map((_, i) => (
                        <ConfettiPiece key={i} index={i} />
                    ))}
                </Box>

                {/* Header */}
                <Box
                    sx={{
                        textAlign: 'center',
                        mb: 6,
                        animation: `${fadeInUp} 0.4s ease-out`,
                        position: 'relative',
                        zIndex: 2,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            width: 400,
                            height: 400,
                            background: `radial-gradient(circle, ${palette.icons.turquoise}08, transparent 70%)`,
                            pointerEvents: 'none',
                            zIndex: -1,
                        },
                    }}
                >
                    {/* C1 Logo */}
                    <Box sx={{ position: 'relative', display: 'inline-block', mb: 3 }}>
                        <Box
                            component="img"
                            src="/dapi/static/images/branding/logo-white/current/256"
                            alt="ConductorOne"
                            sx={{
                                height: 30,
                                width: 'auto',
                                opacity: 0.9,
                                position: 'relative',
                                zIndex: 1,
                            }}
                        />
                    </Box>

                    <Typography
                        variant="h1"
                        sx={{
                            fontWeight: 800,
                            background: titleGradient,
                            backgroundSize: '300% 100%',
                            animation: `${shimmer} 7s ease-in-out infinite alternate`,
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            backgroundClip: 'text',
                            fontSize: { xs: '2.75rem', sm: '4rem', md: '5rem' },
                            letterSpacing: '-0.03em',
                        }}
                    >
                        <FormattedMessage id="year_in_review_title" defaultMessage="2025: Reviewed" />
                    </Typography>
                    <Typography sx={{ mt: 2, fontWeight: 400, color: 'rgba(255, 255, 255, 0.8)', fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' } }}>
                        <FormattedMessage
                            id="year_in_review_subtitle"
                            defaultMessage="Your year in identity security, access management, and compliance"
                        />
                    </Typography>
                </Box>

                {/* ============================================= */}
                {/* SECTION: Your Year in Numbers */}
                {/* ============================================= */}
                <SectionHeader
                    title="Your year in numbers"
                    subtitle="The metrics that defined your 2025"
                    delay={200}
                    color={palette.icons.turquoise}
                />

                {/* Access Grants section */}
                {((): React.ReactElement | null => {
                    const section = SECTIONS[0] // Access Grants
                    const totalGranted = getMetricValue(metrics, 'total_requests_granted')
                    if (totalGranted < MIN_SECTION_THRESHOLD) return null
                    const baseDelay = 400
                    return (
                        <Box
                            key={section.title}
                            sx={{
                                mb: 6,
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                                <Box>
                                    <Typography
                                        variant="h6"
                                        sx={{
                                            display: 'block',
                                            color: 'rgba(255, 255, 255, 0.95)',
                                            fontSize: '1.1rem',
                                            fontWeight: 600,
                                            mb: 1,
                                            animation: `${fadeInUp} 0.4s ease-out ${baseDelay}ms both`,
                                        }}
                                    >
                                        {section.title}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            display: 'block',
                                            color: 'rgba(255, 255, 255, 0.7)',
                                            animation: `${fadeInUp} 0.4s ease-out ${baseDelay}ms both`,
                                        }}
                                    >
                                        <FormattedMessage
                                            id="year_in_review_grants_subtitle"
                                            defaultMessage="Permissions granted throughout the year"
                                        />
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 3 }}>
                                {section.stats.map((stat, statIdx) => (
                                    <StatCell
                                        key={stat.key}
                                        config={stat}
                                        value={getMetricValue(metrics, stat.key)}
                                        delay={baseDelay + 50 + statIdx * 80}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )
                })()}

                {/* Access Revokes section */}
                {((): React.ReactElement | null => {
                    const section = SECTIONS[1] // Access Revokes
                    const totalRevoked = getMetricValue(metrics, 'total_requests_revoked')
                    if (totalRevoked < MIN_SECTION_THRESHOLD) return null
                    const baseDelay = 550
                    return (
                        <Box
                            key={section.title}
                            sx={{
                                mb: 6,
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    display: 'block',
                                    color: 'rgba(255, 255, 255, 0.95)',
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    mb: 1,
                                    animation: `${fadeInUp} 0.4s ease-out ${baseDelay}ms both`,
                                }}
                            >
                                {section.title}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    display: 'block',
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    mb: 3,
                                    animation: `${fadeInUp} 0.4s ease-out ${baseDelay}ms both`,
                                }}
                            >
                                <FormattedMessage
                                    id="year_in_review_revokes_subtitle"
                                    defaultMessage="Permissions revoked to maintain security"
                                />
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 3 }}>
                                {section.stats.map((stat, statIdx) => (
                                    <StatCell
                                        key={stat.key}
                                        config={stat}
                                        value={getMetricValue(metrics, stat.key)}
                                        delay={baseDelay + 50 + statIdx * 80}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )
                })()}

                {/* Access Reviews section with velocity */}
                <AccessReviewsSection
                    metrics={metrics}
                    delay={700}
                />

                {/* Hero: Time Saved */}
                <TimeSavedHero metrics={metrics} delay={150} />

                {/* Integrations & Syncs */}
                {((): React.ReactElement => {
                    const section = SECTIONS[2] // Integrations
                    const baseDelay = 900
                    return (
                        <Box
                            key={section.title}
                            sx={{
                                mb: 6,
                                position: 'relative',
                                zIndex: 2,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    display: 'block',
                                    color: 'rgba(255, 255, 255, 0.95)',
                                    fontSize: '1.1rem',
                                    fontWeight: 600,
                                    mb: 1,
                                    animation: `${fadeInUp} 0.4s ease-out ${baseDelay}ms both`,
                                }}
                            >
                                <FormattedMessage id="year_in_review_integrations_title" defaultMessage="Integrations & syncs" />
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    display: 'block',
                                    color: 'rgba(255, 255, 255, 0.7)',
                                    mb: 3,
                                    animation: `${fadeInUp} 0.4s ease-out ${baseDelay}ms both`,
                                }}
                            >
                                <FormattedMessage
                                    id="year_in_review_integrations_subtitle"
                                    defaultMessage="Connected systems powering your workflows"
                                />
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
                                {section.stats.map((stat, statIdx) => (
                                    <StatCell
                                        key={stat.key}
                                        config={stat}
                                        value={getMetricValue(metrics, stat.key)}
                                        delay={baseDelay + 50 + statIdx * 80}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )
                })()}

                {/* Achievements Row */}
                <AchievementsRow metrics={metrics} delay={250} />

                {/* Monthly Peak Callout */}
                <MonthlyPeakCallout metrics={metrics} delay={300} />

                {/* ============================================= */}
                {/* SECTION: What We Built For You */}
                {/* ============================================= */}
                <SectionHeader
                    title="What we built for you"
                    subtitle="New capabilities shipped in 2025"
                    delay={1000}
                    color={palette.icons.mauve}
                />

                {/* Hero: 3 key stats - Features/Connectors */}
                <HeroStatsRow delay={1050} />

                {/* Features we shipped in 2025 */}
                <FeaturesSection delay={1050} />

                {/* New Connectors */}
                <NewConnectorsSection delay={1200} />

                {/* Footer section */}
                <Box
                    sx={{
                        textAlign: 'center',
                        mt: { xs: 6, sm: 8 },
                        pt: { xs: 5, sm: 6 },
                        borderTop: '1px solid',
                        borderColor: `${palette.icons.mauve}30`,
                        boxShadow: `0 -1px 0 ${palette.icons.mauve}10`,
                        position: 'relative',
                        zIndex: 2,
                        animation: `${fadeInUp} 0.4s ease-out 1700ms both`,
                    }}
                >
                    <Typography
                        sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            mb: 2,
                            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                        }}
                    >
                        <FormattedMessage id="year_in_review_2026_message" defaultMessage="Here's to an even better" />
                        {' '}
                        <Typography
                            component="span"
                            sx={{
                                background: titleGradient,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                fontWeight: 800,
                                fontSize: 'inherit',
                            }}
                        >
                            2026
                        </Typography>
                    </Typography>
                    <Typography
                        sx={{
                            display: 'block',
                            color: 'rgba(255, 255, 255, 0.75)',
                            mb: 4,
                            fontSize: '1rem',
                            maxWidth: 500,
                            mx: 'auto',
                        }}
                    >
                        <FormattedMessage
                            id="year_in_review_thank_you"
                            defaultMessage="Thank you for being part of our journey. We can't wait to see what we'll accomplish together this year."
                        />
                    </Typography>

                    {showDownload && (
                        <Box
                            data-download-button
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 2,
                            }}
                        >
                            <C1LoadingButton
                                startIcon={<C1Icon icon={Download} />}
                                onClick={handleDownload}
                                loading={isDownloading}
                                variant="contained"
                                sx={{
                                    bgcolor: palette.icons.turquoise,
                                    color: palette.brand.dark,
                                    '&:hover': {
                                        bgcolor: lighten(palette.icons.turquoise, 0.1),
                                    },
                                }}
                            >
                                <FormattedMessage id="year_in_review_share" defaultMessage="Share your year" />
                            </C1LoadingButton>

                        </Box>
                    )}
                </Box>
            </Box>
        </>
    )
}

// Modal component
interface YearInReviewModalProps {
    open: boolean
    onClose: () => void
    metrics: YearInReviewMetricEntry[]
}

export const YearInReviewModal: FC<YearInReviewModalProps> = ({ open, onClose, metrics }) => {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen
            TransitionComponent={ModalTransition}
            PaperProps={{
                sx: {
                    bgcolor: palette.brand.dark,
                    overflow: 'auto',
                    background: `
                        radial-gradient(ellipse 800px 600px at 50% 0%, ${palette.icons.turquoise}15 0%, transparent 50%),
                        radial-gradient(ellipse 600px 800px at 100% 50%, ${palette.icons.mauve}12 0%, transparent 50%),
                        radial-gradient(ellipse 800px 600px at 0% 80%, ${palette.brand.main}10 0%, transparent 50%),
                        ${palette.brand.dark}
                    `,
                },
            }}
        >
            <ThemeProvider theme={C1DarkTheme}>
                <Box
                    sx={{
                        position: 'relative',
                        minHeight: '100vh',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: { xs: 2, sm: 4 },
                        animation: `${fadeInUp} 0.6s ease-out`,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: '100%',
                            maxWidth: 1000,
                            height: '100%',
                            background: `radial-gradient(ellipse 600px 400px at 50% 10%, ${glowColor}08, transparent 60%)`,
                            pointerEvents: 'none',
                            zIndex: 0,
                        },
                    }}
                >
                    <C1Button
                        onClick={onClose}
                        variant="text"
                        startIcon={<C1Icon icon={X} />}
                        sx={{
                            position: 'fixed',
                            top: { xs: 6, sm: 16 },
                            right: { xs: 6, sm: 16 },
                            zIndex: 10,
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&:hover': {
                                color: 'white',
                            },
                        }}
                    >
                        <FormattedMessage id="year_in_review_close" defaultMessage="Close" />
                    </C1Button>
                    <Box
                        sx={{
                            width: '100%',
                            maxWidth: 1000,
                            px: { xs: 2, sm: 3 },
                            position: 'relative',
                            zIndex: 1,
                        }}
                    >
                        <YearInReviewContent metrics={metrics} />
                    </Box>
                </Box>
            </ThemeProvider>
        </Dialog>
    )
}

// Inner component that fetches from API
const YearInReviewPageWithApi: FC = () => {
    const hasFeature = useFeatureChecker(FeatureFlagIdYearInReview2025)
    const { data, isLoading } = yearInReviewApi.useGetYearInReview(
        { year: '2025' },
        { actionable: !!hasFeature },
    )

    if (!hasFeature) return null

    if (isLoading) {
        return <ThemeProvider theme={C1DarkTheme}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
                <CircularProgress sx={{ color: palette.icons.turquoise }} />
            </Box>
        </ThemeProvider>
    }

    return <YearInReviewPageInner metrics={data?.metrics ?? []} />
}

// Shared inner component that renders the content
const YearInReviewPageInner: FC<{ metrics: YearInReviewMetricEntry[] }> = ({ metrics }) => {
    return (
        <ThemeProvider theme={C1DarkTheme}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    py: { xs: 4, sm: 6, md: 8 },
                    px: { xs: 2, sm: 4, md: 6 },
                    bgcolor: palette.brand.dark,
                    minHeight: '100vh',
                    background: `
                        radial-gradient(ellipse 1000px 700px at 50% 0%, ${palette.icons.turquoise}12 0%, transparent 50%),
                        radial-gradient(ellipse 800px 1000px at 100% 50%, ${palette.icons.mauve}10 0%, transparent 50%),
                        radial-gradient(ellipse 1000px 700px at 0% 80%, ${palette.brand.main}08 0%, transparent 50%),
                        ${palette.brand.dark}
                    `,
                }}
            >
                <Paper
                    elevation={0}
                    sx={{
                        width: '100%',
                        maxWidth: 1100,
                        bgcolor: 'transparent',
                        borderRadius: C1BorderRadius.large,
                    }}
                >
                    <YearInReviewContent metrics={metrics} />
                </Paper>
            </Box>
        </ThemeProvider>
    )
}
// Standalone page wrapper
export const YearInReviewPage: FC = () => {
    return <YearInReviewPageWithApi />
}
